import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Autocomplete, Box, Button, Chip, FormControlLabel, Grid, InputAdornment, MenuItem, Paper, Stack, Switch,
  TextField, Typography,
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import FormDrawer from '../ui/FormDrawer';
import { FormSection, FormGrid } from '../ui/forms';
import type { MasterFormState } from './MasterFormDrawer';
import {
  getCurrencyReference,
  getCurrencyUsage,
  getMasters,
  type CurrencyReferenceItem,
} from '../../services/platform/masterService';
import {
  CURRENCY_FIELD_LABELS,
  CURRENCY_MAX_DECIMALS,
  CURRENCY_MAX_SORT_ORDER,
  DECIMAL_SEPARATOR_OPTIONS,
  ROUNDING_RULE_OPTIONS,
  SYMBOL_POSITION_OPTIONS,
  THOUSAND_SEPARATOR_OPTIONS,
  cashRoundingSuggestions,
  formatCurrencyAmount,
  nextCurrencySortOrder,
  normalizeCurrencyName,
  roundWithRule,
  validateCurrencyForm,
  type CurrencyExistingRecord,
} from '../../utils/currencyMasterValidation';
import { limitConsecutiveRepeatedCharacters } from '../../utils/descriptionValidation';

interface Props {
  open: boolean;
  editing: boolean;
  form: MasterFormState;
  onChange: (form: MasterFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  editingId?: string;
  saveError?: string | null;
}

const str = (v: unknown) => (v == null ? '' : String(v).trim());
const AMOUNT_INPUT = /^\d*(\.\d*)?$/;

const CREATE_DEFAULTS: Record<string, string | number | boolean> = {
  decimalSeparator: '.',
  thousandSeparator: ',',
  currencyPosition: 'before',
  roundingRule: 'half_up',
  exchangeRateSource: 'manual',
  autoUpdate: false,
  cashRounding: '',
  minPayment: '',
  maxPayment: '',
};

export default function CurrencyFormDrawer({
  open,
  editing,
  form,
  onChange,
  onClose,
  onSubmit,
  loading,
  editingId,
  saveError,
}: Props) {
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const initializedFor = useRef<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const meta = form.metadata;
  const code = str(form.code).toUpperCase();

  const { data: reference, isLoading: referenceLoading } = useQuery({
    queryKey: ['currency-reference'],
    queryFn: getCurrencyReference,
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const { data: existingRaw, isFetching: existingLoading } = useQuery({
    queryKey: ['masters', 'currencies', 'form-siblings'],
    queryFn: () => getMasters('currencies', { limit: 1000, page: 1 }),
    enabled: open,
    staleTime: 0,
  });
  const existing = useMemo<CurrencyExistingRecord[]>(
    () => ((existingRaw?.data ?? []) as CurrencyExistingRecord[]),
    [existingRaw],
  );
  const { data: usage } = useQuery({
    queryKey: ['currency-usage', editingId],
    queryFn: () => getCurrencyUsage(editingId!),
    enabled: open && editing && !!editingId,
    staleTime: 0,
  });

  const baseCurrency = reference?.baseCurrency || 'INR';
  const isBase = !!code && code === baseCurrency;
  const options = reference?.currencies ?? [];
  const selected = useMemo<CurrencyReferenceItem | null>(
    () => options.find((c) => c.code === code) || null,
    [options, code],
  );
  const takenCodes = useMemo(
    () => new Set(existing.filter((r) => r._id !== editingId).map((r) => str(r.code).toUpperCase())),
    [existing, editingId],
  );
  const suggestedSortOrder = useMemo(() => nextCurrencySortOrder(existing, editingId), [existing, editingId]);
  const autoRateSupported = !!selected?.autoRateSupported && !!reference?.baseAutoRateSupported;
  const source = str(meta.exchangeRateSource) || 'manual';
  const sourceDef = reference?.rateSources.find((s) => s.value === source);
  const autoUpdate = meta.autoUpdate === true;
  const decimals = Number(meta.decimalPlaces);
  const precision = Number.isInteger(decimals) && decimals >= 0 && decimals <= CURRENCY_MAX_DECIMALS ? decimals : 2;
  const format = {
    symbol: str(meta.symbol) || code,
    decimals: precision,
    decimalSeparator: str(meta.decimalSeparator) || '.',
    thousandSeparator: meta.thousandSeparator === ' ' ? ' ' : str(meta.thousandSeparator) || ',',
    position: str(meta.currencyPosition) || 'before',
  };
  const roundingRule = str(meta.roundingRule) || 'half_up';
  const roundingDef = ROUNDING_RULE_OPTIONS.find((r) => r.value === roundingRule);

  useEffect(() => {
    if (!open) {
      initializedFor.current = null;
      return;
    }
    const key = editing ? `edit:${editingId}` : 'create';
    if (initializedFor.current === key) return;
    initializedFor.current = key;
    setTouched(new Set());
    if (!editing) {
      onChange({ ...form, code: '', name: '', description: '', isActive: true, sortOrder: 0, metadata: { ...CREATE_DEFAULTS } });
    }
  }, [open, editing, editingId, form, onChange]);

  useEffect(() => {
    if (!open || editing || initializedFor.current !== 'create' || existingLoading) return;
    if (!touched.has('sortOrder') && form.sortOrder !== suggestedSortOrder) {
      onChange({ ...form, sortOrder: suggestedSortOrder });
    }
  }, [open, editing, existingLoading, touched, suggestedSortOrder, form, onChange]);

  const errors = useMemo(
    () => validateCurrencyForm(
      { code, name: form.name, description: form.description, sortOrder: form.sortOrder, metadata: meta },
      { existing, editingId, baseCurrency, isoNumeric: selected?.numeric, autoRateSupported },
    ),
    [code, form.name, form.description, form.sortOrder, meta, existing, editingId, baseCurrency, selected, autoRateSupported],
  );
  const blocking = Object.keys(errors).map((key) => [key, CURRENCY_FIELD_LABELS[key] || key] as const);
  const isValid = blocking.length === 0 && !existingLoading && !referenceLoading;

  const touch = (...keys: string[]) => setTouched((prev) => {
    const next = new Set(prev);
    keys.forEach((k) => next.add(k));
    return next;
  });
  const errorFor = (key: string) => (touched.has(key) || /already/.test(errors[key] || '') ? errors[key] : undefined);
  const focusField = (key: string) => {
    touch(key);
    contentRef.current?.querySelector(`[data-field="${key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const setMeta = (patch: Record<string, string | number | boolean>, ...keys: string[]) => {
    onChange({ ...form, metadata: { ...meta, ...patch } });
    touch(...(keys.length ? keys : Object.keys(patch)));
  };
  const setAmount = (key: string, raw: string) => {
    if (!AMOUNT_INPUT.test(raw)) return;
    setMeta({ [key]: raw });
  };

  const applyCurrency = (option: CurrencyReferenceItem | null) => {
    if (!option) {
      onChange({ ...form, code: '', name: '', metadata: { ...CREATE_DEFAULTS } });
      return;
    }
    const base = option.code === baseCurrency;
    onChange({
      ...form,
      code: option.code,
      name: option.name,
      metadata: {
        ...CREATE_DEFAULTS,
        ...meta,
        isoNumeric: option.numeric,
        symbol: option.symbol,
        decimalPlaces: option.decimals,
        exchangeRateSource: base ? 'manual' : str(meta.exchangeRateSource) || 'manual',
        autoUpdate: false,
        exchangeRate: base ? 1 : '',
      },
    });
    touch('code', 'name');
  };

  const handleSubmit = () => {
    touch(...Object.keys(errors));
    if (!isValid) return;
    onSubmit();
  };

  const usedBy = usage && usage.total > 0
    ? [
      usage.isBase ? 'the base currency' : '',
      usage.tenantDefault ? 'the tenant default currency' : '',
      usage.countries ? `${usage.countries} countr${usage.countries === 1 ? 'y' : 'ies'}` : '',
      usage.currencyProfile ? 'display / settlement / reporting settings' : '',
      usage.payments ? `${usage.payments} payment${usage.payments === 1 ? '' : 's'}` : '',
      usage.wallets ? `${usage.wallets} wallet${usage.wallets === 1 ? '' : 's'}` : '',
    ].filter(Boolean).join(', ')
    : '';
  const actionLabel = editing ? 'Update' : 'Create';
  const exampleA = 2345 / 10 ** (precision + 1);
  const exampleB = 2355 / 10 ** (precision + 1);
  const cashStep = Number(meta.cashRounding);
  const cashExample = 1237 / 10 ** Math.min(precision, 2);
  const updatedAt = str(meta.exchangeRateUpdatedAt);
  const amountProps = {
    startAdornment: <InputAdornment position="start">{format.symbol || '¤'}</InputAdornment>,
  };

  return (
    <FormDrawer
      open={open}
      title={`${editing ? 'Edit' : 'Add'} Currency`}
      subtitle="Pick an ISO 4217 currency — code, ISO numeric code, symbol and decimal places fill in automatically."
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={actionLabel}
      loading={loading}
      disableSubmit={!isValid}
      submitHint={blocking.length
        ? `${blocking.length} item${blocking.length === 1 ? '' : 's'} to complete before you can ${actionLabel.toLowerCase()} — see the checklist at the top`
        : undefined}
      width={720}
      contentRef={contentRef}
    >
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
      {blocking.length > 0 && (
        <Alert severity={code ? 'warning' : 'info'} sx={{ mb: 2 }} data-validation-summary>
          <Typography variant="body2" fontWeight={600} gutterBottom={!!code}>
            {code ? `Complete these to enable ${actionLabel}:` : 'Start by picking a currency from the ISO 4217 list.'}
          </Typography>
          {code && (
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {blocking.map(([key, label]) => (
                <Chip
                  key={key}
                  size="small"
                  label={`${label}: ${errors[key]}`}
                  color="warning"
                  variant="outlined"
                  onClick={() => focusField(key)}
                  sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
                />
              ))}
            </Stack>
          )}
        </Alert>
      )}
      {editing && (
        <Alert severity={usedBy ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {usedBy
            ? `Used by ${usedBy}. The code and ISO numeric code are locked so existing amounts keep their currency.`
            : 'The code and ISO numeric code are locked after creation.'}
        </Alert>
      )}

      <FormSection title="Currency" divider>
        <FormGrid>
          {!editing && (
            <Grid item xs={12} data-field="code">
              <Autocomplete<CurrencyReferenceItem>
                options={options}
                loading={referenceLoading || existingLoading}
                value={selected}
                onChange={(_, value) => applyCurrency(value)}
                onBlur={() => touch('code')}
                getOptionLabel={(o) => `${o.code} — ${o.name}`}
                isOptionEqualToValue={(o, v) => o.code === v.code}
                getOptionDisabled={(o) => takenCodes.has(o.code)}
                filterOptions={(opts, s) => {
                  const q = s.inputValue.trim().toLowerCase();
                  if (!q) return opts;
                  return opts.filter((o) => o.name.toLowerCase().includes(q)
                    || o.code.toLowerCase().includes(q)
                    || o.numeric === q
                    || o.symbol.toLowerCase() === q
                    || o.countries.some((c) => c.toLowerCase() === q));
                }}
                renderOption={(props, o) => (
                  <li {...props} key={o.code}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                      <Typography variant="body2" fontFamily="monospace" fontWeight={700} sx={{ width: 40 }}>{o.code}</Typography>
                      <Typography variant="body2" sx={{ flex: 1 }}>{o.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{o.symbol} · {o.numeric}</Typography>
                      {takenCodes.has(o.code) && <Chip size="small" label="Already added" />}
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    size="small"
                    label="Currency (ISO 4217)"
                    placeholder="Search by name, code, symbol or country code"
                    error={!!errorFor('code')}
                    helperText={errorFor('code') || `${options.length ? `${options.length - takenCodes.size} currencies available` : 'Loading currencies…'} · already added ones are disabled`}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} data-field="name">
            <TextField
              fullWidth
              size="small"
              required
              label="Currency name"
              value={form.name}
              disabled={!code}
              onChange={(e) => {
                onChange({ ...form, name: limitConsecutiveRepeatedCharacters(e.target.value).replace(/^\s+/, '') });
                touch('name');
              }}
              onBlur={() => {
                touch('name');
                const clean = normalizeCurrencyName(form.name);
                if (clean !== form.name) onChange({ ...form, name: clean });
              }}
              error={!!errorFor('name')}
              helperText={errorFor('name') || 'Unique · 2–60 characters'}
              inputProps={{ maxLength: 60 }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Code"
              value={code}
              helperText={editing ? 'Locked' : 'Auto from currency'}
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'monospace' },
                endAdornment: (
                  <InputAdornment position="end">
                    <Lock sx={{ fontSize: 14, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} data-field="isoNumeric">
            <TextField
              fullWidth
              size="small"
              label="ISO numeric code"
              value={str(meta.isoNumeric) || selected?.numeric || ''}
              error={!!errors.isoNumeric && !!code}
              helperText={(code && errors.isoNumeric) || '3 digits · auto (ISO 4217)'}
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'monospace' },
                endAdornment: (
                  <InputAdornment position="end">
                    <Lock sx={{ fontSize: 14, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="sortOrder">
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label="Sort order"
              value={form.sortOrder >= 1 ? form.sortOrder : ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (/[-+,eE.]/.test(raw)) return;
                onChange({ ...form, sortOrder: raw === '' ? 0 : Number(raw) });
                touch('sortOrder');
              }}
              onBlur={() => touch('sortOrder')}
              error={!!errorFor('sortOrder')}
              helperText={errorFor('sortOrder') || `Unique · next available is ${suggestedSortOrder}`}
              inputProps={{ min: 1, max: CURRENCY_MAX_SORT_ORDER, step: 1 }}
              InputProps={{
                endAdornment: form.sortOrder !== suggestedSortOrder && !editing ? (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => { onChange({ ...form, sortOrder: suggestedSortOrder }); touch('sortOrder'); }}>
                      Use {suggestedSortOrder}
                    </Button>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {editing
                ? 'Use the status toggle in the Currencies list to activate or deactivate this currency.'
                : 'New currencies are created as Active. Use the status toggle in the list to deactivate.'}
            </Typography>
          </Grid>
          <Grid item xs={12} data-field="description">
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Description"
              value={form.description}
              onChange={(e) => {
                onChange({ ...form, description: limitConsecutiveRepeatedCharacters(e.target.value) });
                touch('description');
              }}
              onBlur={() => touch('description')}
              error={!!errorFor('description')}
              helperText={errorFor('description') || `${str(form.description).length}/500 · no repeated characters or words`}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
        </FormGrid>
      </FormSection>

      <FormSection title="Display format" divider>
        <FormGrid>
          <Grid item xs={6} sm={3} data-field="symbol">
            <TextField
              fullWidth
              size="small"
              required
              label="Symbol"
              value={str(meta.symbol) === '' ? '' : String(meta.symbol)}
              disabled={!code}
              onChange={(e) => setMeta({ symbol: e.target.value.replace(/\d/g, '').slice(0, 6) })}
              onBlur={() => { touch('symbol'); setMeta({ symbol: str(meta.symbol) }); }}
              error={!!errorFor('symbol')}
              helperText={errorFor('symbol') || `1–6 chars, no digits${selected ? ` · ISO: ${selected.symbol}` : ''}`}
              inputProps={{ maxLength: 6 }}
            />
          </Grid>
          <Grid item xs={6} sm={3} data-field="decimalPlaces">
            <TextField
              select
              fullWidth
              size="small"
              required
              label="Decimal places"
              value={str(meta.decimalPlaces)}
              disabled={!code}
              onChange={(e) => setMeta({ decimalPlaces: Number(e.target.value) })}
              onBlur={() => touch('decimalPlaces')}
              error={!!errorFor('decimalPlaces')}
              helperText={errorFor('decimalPlaces')
                || (selected && selected.decimals !== precision
                  ? `ISO standard for ${code} is ${selected.decimals}`
                  : `0–${CURRENCY_MAX_DECIMALS}${selected ? ` · ISO standard ${selected.decimals}` : ''}`)}
              FormHelperTextProps={{ sx: selected && selected.decimals !== precision && !errorFor('decimalPlaces') ? { color: 'warning.main' } : undefined }}
            >
              {Array.from({ length: CURRENCY_MAX_DECIMALS + 1 }, (_, n) => (
                <MenuItem key={n} value={String(n)}>{n} {n === 0 ? '(whole units)' : `(${(1 / 10 ** n).toFixed(n)})`}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} data-field="currencyPosition">
            <TextField
              select
              fullWidth
              size="small"
              label="Symbol position"
              value={format.position}
              disabled={!code}
              onChange={(e) => setMeta({ currencyPosition: e.target.value })}
              helperText={`e.g. ${formatCurrencyAmount(1250, format)}`}
            >
              {SYMBOL_POSITION_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label} — {formatCurrencyAmount(1250, { ...format, position: o.value })}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} data-field="decimalSeparator">
            <TextField
              select
              fullWidth
              size="small"
              label="Decimal separator"
              value={format.decimalSeparator}
              disabled={!code || precision === 0}
              onChange={(e) => setMeta({ decimalSeparator: e.target.value }, 'decimalSeparator', 'thousandSeparator')}
              helperText={precision === 0 ? 'Not used — no decimal places' : 'Separates the whole and fractional part'}
            >
              {DECIMAL_SEPARATOR_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} data-field="thousandSeparator">
            <TextField
              select
              fullWidth
              size="small"
              label="Thousand separator"
              value={format.thousandSeparator}
              disabled={!code}
              onChange={(e) => setMeta({ thousandSeparator: e.target.value }, 'decimalSeparator', 'thousandSeparator')}
              error={!!errors.thousandSeparator && !!code}
              helperText={(code && errors.thousandSeparator) || 'Must differ from the decimal separator'}
            >
              {THOUSAND_SEPARATOR_OPTIONS.map((o) => (
                <MenuItem key={o.label} value={o.value} disabled={o.value === format.decimalSeparator}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 3 }} alignItems={{ sm: 'center' }}>
                <Typography variant="caption" color="text.secondary">Preview</Typography>
                <Typography variant="h6" fontFamily="monospace" fontWeight={700}>
                  {code ? formatCurrencyAmount(1234567.891, format) : '—'}
                </Typography>
                <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                  {code ? formatCurrencyAmount(-42.5, format) : ''}
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </FormGrid>
      </FormSection>

      <FormSection title={`Exchange rate (base currency: ${baseCurrency})`} divider>
        <FormGrid>
          {isBase ? (
            <Grid item xs={12}>
              <Alert severity="info">
                {code} is the platform base currency (set in Locale settings). Its exchange rate is always 1 and it is never auto-updated.
              </Alert>
            </Grid>
          ) : (
            <>
              <Grid item xs={12} sm={6} data-field="exchangeRateSource">
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Rate source"
                  value={source}
                  disabled={!code}
                  onChange={(e) => setMeta(
                    { exchangeRateSource: e.target.value, ...(e.target.value === 'manual' ? { autoUpdate: false } : {}) },
                    'exchangeRateSource', 'autoUpdate',
                  )}
                  helperText={sourceDef?.description || 'How the exchange rate is kept up to date'}
                >
                  {(reference?.rateSources ?? [{ value: 'manual', label: 'Manual entry', automatic: false, description: '' }]).map((s) => (
                    <MenuItem key={s.value} value={s.value} disabled={s.automatic && !!code && !autoRateSupported}>
                      {s.label}
                      {s.automatic && !!code && !autoRateSupported ? ` — not available for ${code}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} data-field="autoUpdate">
                <FormControlLabel
                  control={(
                    <Switch
                      checked={autoUpdate}
                      disabled={!code || source !== 'api' || !autoRateSupported}
                      onChange={(e) => setMeta({ autoUpdate: e.target.checked }, 'autoUpdate', 'exchangeRate')}
                    />
                  )}
                  label="Auto update rate"
                />
                <Typography variant="caption" display="block" color={errorFor('autoUpdate') ? 'error' : 'text.secondary'}>
                  {errorFor('autoUpdate')
                    || (!code
                      ? 'Pick a currency first'
                      : !autoRateSupported
                        ? `Frankfurter does not publish ${selected?.autoRateSupported ? baseCurrency : code} rates — use manual entry`
                        : source !== 'api'
                          ? 'Available when the rate source is Frankfurter API'
                          : autoUpdate
                            ? 'Fetched on save, then refreshed once a day'
                            : 'Off — the rate stays as entered')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} data-field="exchangeRate">
                <TextField
                  fullWidth
                  size="small"
                  required={!autoUpdate}
                  label={`Exchange rate (1 ${baseCurrency} = ? ${code || 'units'})`}
                  value={str(meta.exchangeRate) === '' ? '' : String(meta.exchangeRate)}
                  disabled={!code || autoUpdate}
                  onChange={(e) => setAmount('exchangeRate', e.target.value)}
                  onBlur={() => touch('exchangeRate')}
                  error={!!errorFor('exchangeRate')}
                  helperText={errorFor('exchangeRate')
                    || (autoUpdate
                      ? `${str(meta.exchangeRate) ? 'Last fetched rate' : 'Fetched live from Frankfurter when you save'}${updatedAt ? ` · updated ${new Date(updatedAt).toLocaleString()}` : ''}`
                      : `How many ${code || 'units'} equal 1 ${baseCurrency} · > 0, up to 8 decimals`)}
                  InputProps={{ endAdornment: code ? <InputAdornment position="end">{code}</InputAdornment> : undefined }}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                {code && Number(meta.exchangeRate) > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    1 {baseCurrency} = {Number(meta.exchangeRate)} {code}
                    {' · '}1 {code} ≈ {(1 / Number(meta.exchangeRate)).toPrecision(4)} {baseCurrency}
                  </Typography>
                )}
              </Grid>
            </>
          )}
        </FormGrid>
      </FormSection>

      <FormSection title="Rounding & payment limits">
        <FormGrid>
          <Grid item xs={12} sm={6} data-field="roundingRule">
            <TextField
              select
              fullWidth
              size="small"
              label="Rounding rule"
              value={roundingRule}
              disabled={!code}
              onChange={(e) => setMeta({ roundingRule: e.target.value })}
              helperText={roundingDef?.description}
            >
              {ROUNDING_RULE_OPTIONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
            {code && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Applied to converted amounts at {precision} decimal place{precision === 1 ? '' : 's'}:
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  {exampleA} → {roundWithRule(exampleA, precision, roundingRule).toFixed(precision)}
                  {'   '}
                  {exampleB} → {roundWithRule(exampleB, precision, roundingRule).toFixed(precision)}
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={6} data-field="cashRounding">
            <TextField
              fullWidth
              size="small"
              label="Cash rounding"
              value={str(meta.cashRounding) === '' ? '' : String(meta.cashRounding)}
              disabled={!code}
              onChange={(e) => setAmount('cashRounding', e.target.value)}
              onBlur={() => touch('cashRounding')}
              error={!!errorFor('cashRounding')}
              helperText={errorFor('cashRounding')
                || (cashStep > 0
                  ? `Cash totals round to the nearest ${formatCurrencyAmount(cashStep, format)} — e.g. ${formatCurrencyAmount(cashExample, format)} → ${formatCurrencyAmount(Math.round(cashExample / cashStep) * cashStep, format)}`
                  : 'Smallest coin / note for cash payments · blank or 0 = off')}
              InputProps={amountProps}
            />
            {code && (
              <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {cashRoundingSuggestions(precision).map((v) => (
                  <Chip
                    key={v}
                    size="small"
                    label={v.toFixed(Math.min(precision, 2))}
                    variant={cashStep === v ? 'filled' : 'outlined'}
                    color={cashStep === v ? 'primary' : 'default'}
                    onClick={() => setMeta({ cashRounding: v })}
                  />
                ))}
                <Chip size="small" label="Off" variant={cashStep > 0 ? 'outlined' : 'filled'} onClick={() => setMeta({ cashRounding: '' })} />
              </Stack>
            )}
          </Grid>
          <Grid item xs={12} sm={6} />
          <Grid item xs={12} sm={6} data-field="minPayment">
            <TextField
              fullWidth
              size="small"
              label="Minimum payment"
              value={str(meta.minPayment) === '' ? '' : String(meta.minPayment)}
              disabled={!code}
              onChange={(e) => setAmount('minPayment', e.target.value)}
              onBlur={() => touch('minPayment', 'maxPayment')}
              error={!!errorFor('minPayment')}
              helperText={errorFor('minPayment') || `0 or more · up to ${precision} decimal${precision === 1 ? '' : 's'} (currency precision)`}
              InputProps={amountProps}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="maxPayment">
            <TextField
              fullWidth
              size="small"
              label="Maximum payment"
              value={str(meta.maxPayment) === '' ? '' : String(meta.maxPayment)}
              disabled={!code}
              onChange={(e) => setAmount('maxPayment', e.target.value)}
              onBlur={() => touch('maxPayment')}
              error={!!errorFor('maxPayment')}
              helperText={errorFor('maxPayment') || `Must be ≥ minimum · blank or 0 = no limit`}
              InputProps={amountProps}
            />
          </Grid>
        </FormGrid>
      </FormSection>
    </FormDrawer>
  );
}
